<template>
  <div>
    <a-popover
      v-model="visible"
      trigger="click"
      placement="bottomLeft"
      overlayClassName="header-notice-wrapper"
      :getPopupContainer="() => $refs.noticeRef.parentElement"
      :autoAdjustOverflow="true"
      :arrowPointAtCenter="true"
      :overlayStyle="{ width: '300px', top: '50px' }"
      @visibleChange="onVisibleChange"
    >
      <template slot="content">
        <a-spin :spinning="loading">
          <a-tabs v-model="queryParam.noticeType" @change="changeType">
            <a-tab-pane v-for="d in typeOptions" :key="d.dictValue" :tab="d.dictLabel">
              <a-list style="max-height: 300px; overflow:auto;cursor: pointer;">
                <div
                  v-if="showLoadingMore"
                  slot="loadMore"
                  :style="{ textAlign: 'center', marginTop: '12px', height: '32px', lineHeight: '32px' }"
                >
                  <a-spin v-if="loadingMore" />
                  <a-button v-else @click="onLoadMore">
                    查看更多
                  </a-button>
                </div>
                <a-list-item v-for="(item, index) in list" :key="index">
                  <a-list-item-meta :description="item.createTime">
                    <a slot="title" @click="handleTitleClick(item)" style="display: flex; align-items: center;">
                      <span v-if="item.pendingAudit" class="pending-dot" />
                      <ellipsis :length="30" tooltip style="flex: 1;">{{ item.videoContent || item.noticeTitle }}</ellipsis>
                    </a>
                  </a-list-item-meta>
                </a-list-item>
              </a-list>
            </a-tab-pane>
          </a-tabs>
        </a-spin>
      </template>
      <span class="header-notice" ref="noticeRef">
        <a-badge :count="unreadCount" :overflow-count="99" :offset="[-2, 2]">
          <a-icon style="font-size: 20px;" type="bell" />
        </a-badge>
      </span>
    </a-popover>
    <notice-detail ref="noticeDetail" :typeOptions="typeOptions" />
  </div>
</template>

<script>
import { pendingAuditList, pendingAuditIds } from '@/api/system/notice'
import Ellipsis from '@/components/Ellipsis'
import NoticeDetail from './NoticeDetail'

export default {
  name: 'HeaderNotice',
  components: {
    Ellipsis,
    NoticeDetail
  },
  data () {
    return {
      loading: false,
      loadingMore: false,
      showLoadingMore: true,
      visible: false,
      pollTimer: null,
      // 提示音 audio 元素
      audioEl: null,
      // 待审核动态ID集合（用于列表红点 + 角标）
      pendingIds: [],
      queryParam: {
        pageNum: 1,
        pageSize: 5,
        status: 1,
        noticeType: '3'
      },
      list: [],
      typeOptions: []
    }
  },
  computed: {
    /** 铃铛角标 = 待审核动态总数，始终跟随 pendingIds，不随弹窗开关清零 */
    unreadCount () {
      return this.pendingIds.length
    },
    /** 当前是否为"后台待办"标签（noticeType=3），该标签数据由待审核动态实时提供，与角标同源 */
    isPendingTab () {
      return String(this.queryParam.noticeType) === '3'
    }
  },
  created () {
    this.getDicts('sys_notice_type').then(response => {
      this.typeOptions = response.data
    })
    this.startPoll()
    // 审核动态后，video 页面派发事件，立即刷新角标与待办红点
    window.addEventListener('notice-badge-refresh', this.onAuditRefreshed)
  },
  beforeDestroy () {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
    window.removeEventListener('notice-badge-refresh', this.onAuditRefreshed)
  },
  methods: {
    /** 从通知简介中解析动态ID，例如"动态ID：707914387893194752"（仅非待办标签的公告兼容使用） */
    parseVideoId (item) {
      const text = (item.noticeInfo || '') + (item.noticeContent || '')
      const m = text.match(/动态ID[:：]\s*(\d+)/)
      return m ? Number(m[1]) : null
    },
    /** 根据待审核ID集合，标记列表项红点（带红点排前面）。角标由 computed unreadCount 提供，不在此处理 */
    markPendingAndBadge () {
      const idSet = new Set(this.pendingIds.map(Number))
      this.list.forEach(item => {
        const vid = this.parseVideoId(item)
        item.pendingAudit = vid != null && idSet.has(vid)
      })
      // 带红点（待审核）的排到前面，保持原相对顺序
      this.list = this.list.slice().sort((a, b) => (b.pendingAudit ? 1 : 0) - (a.pendingAudit ? 1 : 0))
    },
    /** 加载待办列表：后台待办标签直接取待审核动态（与角标同源实时），其他标签取公告列表 */
    getList () {
      this.loading = true
      if (this.isPendingTab) {
        // 方案A：待办数据直接来自待审核动态，实时、与角标完全一致
        pendingAuditList().then(response => {
          const records = Array.isArray(response.data) ? response.data : []
          // 每条都是待审核，自带红点；按创建时间倒序已由后端保证
          this.list = records.map(item => ({ ...item, pendingAudit: true }))
          this.total = records.length
          this.showLoadingMore = false
          this.loading = false
        }).catch(() => {
          this.list = []
          this.total = 0
          this.loading = false
        })
        return
      }
      // 其他标签：原公告列表逻辑
      listNotice(this.queryParam).then(response => {
          const data = response.data
          // list 接口直接返回数组；兼容分页对象结构
          const records = Array.isArray(data) ? data : (data.records || [])
          const total = Array.isArray(data) ? records.length : (data.total || 0)
          this.list = records
          this.total = total
          if (total <= this.queryParam.pageNum * this.queryParam.pageSize) {
            this.showLoadingMore = false
          } else {
            this.showLoadingMore = true
          }
          this.loading = false
          // 非待办标签：根据待审核集合标记红点
          this.markPendingAndBadge()
        }
      )
    },
    /** popover 显隐变化：打开时重置并加载后台待办列表（角标由 computed 跟随 pendingIds，不在此清零） */
    onVisibleChange (open) {
      if (open) {
        this.resetQuery()
        this.getList()
      }
    },
    resetQuery () {
      this.queryParam = {
        pageNum: 1,
        pageSize: 5,
        status: 1,
        noticeType: '3'
      }
      this.list = []
    },
    changeType (key) {
      this.resetQuery()
      this.queryParam.noticeType = key
      this.getList()
    },
    onLoadMore () {
      this.loadingMore = true
      this.queryParam.pageNum++
      this.getList()
      this.loadingMore = false
    },
    /** 启动轮询：每 60 秒刷新待审核动态集合 */
    startPoll () {
      this.refreshPendingIds()
      this.pollTimer = setInterval(() => {
        this.refreshPendingIds()
      }, 60000)
    },
    /** 审核动态后触发：立即刷新角标与待办红点 */
    onAuditRefreshed () {
      pendingAuditIds().then(response => {
        const ids = Array.isArray(response.data) ? response.data : []
        this.pendingIds = ids
        // 若弹窗已打开且当前是后台待办列表，重新拉取列表以更新排序与红点
        if (this.visible && String(this.queryParam.noticeType) === '3') {
          this.getList()
        }
      }).catch(() => {})
    },
    /** 拉取待审核动态ID集合，刷新列表红点 + 角标 */
    refreshPendingIds () {
      pendingAuditIds().then(response => {
        const ids = Array.isArray(response.data) ? response.data : []
        const newCount = ids.length
        // 数量增加时播放提示音
        if (newCount > this.pendingIds.length) {
          this.playNotifySound()
        }
        this.pendingIds = ids
        // 若弹窗已打开：待办标签直接重新拉取实时列表；其他标签刷新红点标记
        if (this.visible) {
          if (this.isPendingTab) {
            this.getList()
          } else {
            this.markPendingAndBadge()
          }
        }
      }).catch(() => {})
    },
    /** 播放提示音（使用固定音频文件 shenhe.mp3） */
    playNotifySound () {
      try {
        if (!this.audioEl) {
          this.audioEl = new Audio(process.env.BASE_URL + 'audio/shenhe.mp3')
          this.audioEl.preload = 'auto'
        }
        // 重新播放：重置到开头，避免上一次未播完
        this.audioEl.currentTime = 0
        const p = this.audioEl.play()
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            // 浏览器自动播放策略限制时静默忽略
          })
        }
      } catch (e) {
        // 忽略音频播放异常（如浏览器策略限制）
      }
    },
    /** 点击通知标题：查看详情，若为后台待办（待审核动态）则跳转动态审核页 */
    handleTitleClick (item) {
      if (item.pendingAudit) {
        // 后台待办：直接跳转动态审核列表（带待审核筛选），定位到对应动态
        this.$router.push({ path: '/video/video', query: { videoState: '1' } })
        this.visible = false
        return
      }
      this.$refs.noticeDetail.getNotice(item.id)
    }
  }
}
</script>

<style lang="css">
  .header-notice-wrapper {
    top: 50px !important;
  }
  .pending-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #f5222d;
    margin-right: 6px;
    flex: 0 0 auto;
  }
</style>
<style lang="less" scoped>
  .header-notice{
    display: inline-block;
    transition: all 0.3s;

    span {
      vertical-align: initial;
    }
  }
</style>
